import { create } from 'zustand';
import { authCookies } from '../utils/cookies';
import { authService } from '../services/authService';

export const useAuthStore = create((set, get) => ({
  user: null,
  token: null,
  loading: false,
  error: null,

  // Load persisted session (from cookies) on app mount
  hydrate: () => {
    const token = authCookies.getToken();
    const user = authCookies.getUser();
    if (token) set({ token, user });
  },

  login: async (credentials) => {
    set({ loading: true, error: null });
    try {
      const { token, user } = await authService.login(credentials);
      authCookies.setToken(token);
      authCookies.setUser(user);
      set({ token, user, loading: false });
      // Return the user so callers (useAuth) can decide routing based on
      // e.g. has_social_links without re-reading the store.
      return { success: true, user };
    } catch (err) {
      const message = err?.response?.data?.message || 'Login failed';
      set({ loading: false, error: message });
      return { success: false, error: message };
    }
  },

  register: async (payload) => {
    set({ loading: true, error: null });
    try {
      const { token, user } = await authService.register(payload);
      authCookies.setToken(token);
      authCookies.setUser(user);
      set({ token, user, loading: false });
      return { success: true, user };
    } catch (err) {
      const message = err?.response?.data?.message || 'Registration failed';
      set({ loading: false, error: message });
      return { success: false, error: message };
    }
  },

  logout: async () => {
    try {
      await authService.logout();
    } catch {
      /* ignore logout network errors */
    }
    authCookies.clear();
    set({ user: null, token: null });
  },

  updateProfile: async (patch) => {
    set({ loading: true, error: null });
    try {
      const updates = await authService.updateProfile(patch);
      const merged = { ...get().user, ...updates };
      authCookies.setUser(merged);
      set({ user: merged, loading: false });
      return { success: true, user: merged };
    } catch (err) {
      const message = err?.response?.data?.message || 'Profile update failed';
      set({ loading: false, error: message });
      return { success: false, error: message };
    }
  },

  clearSession: () => {
    authCookies.clear();
    set({ user: null, token: null });
  },

  get isAuthenticated() {
    return Boolean(get().token);
  },
}));
